// packages/core/src/learning/readTime.ts
// ─────────────────────────────────────────────────────────────────────────
// READ-TIME ESTIMATE — derived from a lesson's actual prose, not authored.
//
// The header used to print Lesson.estimatedReadMinutes, a hand-typed integer
// that was uniformly wrong (every lesson "5 min"). Reading time is a pure
// function of how many words the lesson contains, so we compute it from the
// blocks at a fixed words-per-minute rate. Every lesson gets an honest,
// per-lesson number with zero content edits.
//
// WPM: 200 is the standard adult non-fiction silent-reading rate. Slightly
// generous for a learning context (readers pause on money concepts), which is
// the safe direction — better to under-promise the number than rush someone.
//
// PURE. No DOM, no fetch. The web and RN apps both call estimateReadMinutes().
// Counts words across every text-bearing Block variant:
//   paragraph | callout | example | key_takeaway → `text`
//   bullet_list                                  → `items[]`
// Non-prose block types (should any be added later) contribute nothing.
// ─────────────────────────────────────────────────────────────────────────

import type { Block } from './LearningTypes';

/** Standard adult non-fiction silent-reading rate (words per minute). */
export const READ_WPM = 200;

/** Count whitespace-delimited words in a string. Empty/whitespace → 0. */
function countWords(text: string): number {
	const trimmed = text.trim();
	if (!trimmed) return 0;
	return trimmed.split(/\s+/).length;
}

/** Total words across a lesson's blocks (every text-bearing variant). */
export function countLessonWords(blocks: Block[]): number {
	let words = 0;
	for (const block of blocks) {
		switch (block.type) {
			case 'paragraph':
			case 'callout':
			case 'example':
			case 'key_takeaway':
				words += countWords(block.text);
				break;
			case 'bullet_list':
				for (const item of block.items) words += countWords(item);
				break;
			// No default: the switch is exhaustive over Block. If a new
			// non-text variant is added, it simply contributes 0 words until
			// this helper is taught how to count it.
		}
	}
	return words;
}

/**
 * Estimated reading time in MINUTES (fractional), derived from the lesson's
 * prose at READ_WPM. Returns a real fractional value (e.g. 2.3), so callers
 * can format to whatever precision they want. A lesson with no countable text
 * returns 0 — the caller decides how to present that.
 */
export function estimateReadMinutes(blocks: Block[]): number {
	const words = countLessonWords(blocks);
	if (words <= 0) return 0;
	return words / READ_WPM;
}

/**
 * Convenience formatter for read time, matching the "honest small numbers"
 * goal. Sub-minute reads show seconds ("40 sec"); otherwise one decimal in the
 * minutes band ("2.3 min"), collapsing a trailing .0 to a whole number
 * ("3 min"). Returns '' for a zero/empty lesson so the caller can omit it.
 */
export function formatReadTime(minutes: number): string {
	if (minutes <= 0) return '';
	if (minutes < 1) {
		const sec = Math.max(1, Math.round(minutes * 60));
		return `${sec} sec`;
	}
	const rounded = Math.round(minutes * 10) / 10;
	const label = Number.isInteger(rounded)
		? String(rounded)
		: rounded.toFixed(1);
	return `${label} min`;
}
